import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Paper, Typography, Grid, Card, CardContent, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Breadcrumbs, Link as MuiLink } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Home as HomeIcon } from '@mui/icons-material';

interface PricingPlan {
  _id?: string;
  key: 'inference' | 'training' | 'foundation' | string;
  title: string;
  subtitle: string;
  description?: string;
  priceAnnualUSD: number;
  sla: string;
  features: string[];
  featureDetails?: { title: string; description: string }[];
  order: number;
  isActive: boolean;
}

const defaultPlans: PricingPlan[] = [
  { key: 'inference', title: 'E‑Unify Inference', subtitle: 'Entry / Pilot', description: 'Fast adoption for pilots and read‑heavy use. Essential security, basic audit, baseline accessibility. $6,000/year or $650/month. Optional 90‑day pilot: $1,500/mo × 3 credited toward annual.', priceAnnualUSD: 6000, sla: '99.5%', features: [
    '1 environment (Production)', 'OAuth/OIDC (standard)', 'TLS, IP allowlisting, basic audit log', 'Daily backups (7-day retention)', 'Email support (2 business-day response)', 'Section 508 baseline' 
  ], featureDetails: [
    { title: 'Environment Scope', description: 'Single production environment reduces operational overhead for pilots.' },
    { title: 'Identity', description: 'OAuth/OIDC SSO; suitable for most enterprise IdPs.' },
    { title: 'Backups', description: 'Daily snapshots with 7‑day retention for recovery from user error.' }
  ], order: 1, isActive: true },
  { key: 'training', title: 'E‑Unify Training', subtitle: 'Program', description: 'Designed for multi‑team scale and heavier workloads. Adds staging, advanced identity, and monitoring to support program governance.', priceAnnualUSD: 24000, sla: '99.9%', features: [
    '2 environments (Prod + Staging)', 'SAML/OIDC + MFA; optional SCIM', 'Advanced audit logs; retention controls', 'Hourly + daily backups', 'Monitoring dashboards & alerting', '508 verified; SOC2-ready controls'
  ], featureDetails: [
    { title: 'Advanced Identity', description: 'SAML/OIDC with MFA enforcement; SCIM user sync optional.' },
    { title: 'Observability', description: 'Central dashboards, alerting, and retention controls.' },
    { title: 'Change Safety', description: 'Staging environment enables safe rollout and UAT.' }
  ], order: 2, isActive: true },
  { key: 'foundation', title: 'E‑Unify Foundation', subtitle: 'Enterprise', description: 'Enterprise‑grade posture with HA/DR, private networking, and ATO support artifacts. Suitable for mission systems across components.', priceAnnualUSD: 85000, sla: '99.95%', features: [
    'Dev + Stage + Prod + DR region', 'Private VPC networking; cust.-managed KMS keys', 'Continuous backup (PITR) + cross-region DR', 'ATO support package; security scans', '24x7 critical support; TAM; 4h Sev-1', 'Capacity planning & scaling SLAs'
  ], featureDetails: [
    { title: 'Networking', description: 'Private VPC/PrivateLink options and customer‑managed keys (KMS).' },
    { title: 'Resilience', description: 'Point‑in‑time recovery and cross‑region disaster recovery.' },
    { title: 'Compliance', description: 'ATO support pack with inherited controls mapping and scan reports.' }
  ], order: 3, isActive: true }
];

const usePricingPlans = () => {
  const [plans, setPlans] = useState<PricingPlan[]>(defaultPlans);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch('/api/v1/pricing-plans');
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) {
          throw new Error('Non-JSON response');
        }
        const json = await res.json();
        if (!cancelled && json?.success && Array.isArray(json.data) && json.data.length) {
          // Ensure order
          const ordered = json.data.sort((a: PricingPlan, b: PricingPlan) => (a.order ?? 0) - (b.order ?? 0));
          setPlans(ordered);
        }
      } catch (e: any) {
        setError('Live pricing plans unavailable. Showing default tiers.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const save = async (plan: PricingPlan) => {
    const method = plan._id ? 'PUT' : 'POST';
    const url = plan._id ? `/api/v1/pricing-plans/${plan._id}` : '/api/v1/pricing-plans';
    const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(plan), credentials: 'include' });
    const json = await res.json();
    if (!json?.success) throw new Error(json?.error || 'Save failed');
    // Refresh list
    const listRes = await fetch('/api/v1/pricing-plans');
    const listJson = await listRes.json();
    if (listJson?.success) setPlans(listJson.data.sort((a: PricingPlan, b: PricingPlan) => (a.order ?? 0) - (b.order ?? 0)));
  };

  return { plans, loading, error, save } as const;
};

const planAccent = (key: string) => {
  switch (key) {
    case 'inference':
      return '#003366'; // USCIS blue
    case 'training':
      return '#0056b3'; // deeper blue
    case 'foundation':
      return '#B31B1B'; // USCIS red accent
    default:
      return '#003366';
  }
};

const PriceCard: React.FC<{ plan: PricingPlan; onEdit: (p: PricingPlan) => void }>= ({ plan, onEdit }) => (
  <Card
    role="button"
    tabIndex={0}
    aria-label={`Edit ${plan.title} pricing plan`}
    onClick={() => onEdit(plan)}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(plan); } }}
    sx={{
      height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2,
      border: '1px solid #e0e0e0', cursor: 'pointer',
      outline: 'none',
      '&:focus-visible': { outline: '3px solid #003366', outlineOffset: 2 },
      '&:hover': { boxShadow: 3 }
    }}
  >
    {/* Top accent bar for visual categorization, meets contrast requirements */}
    <Box aria-hidden sx={{ height: 6, width: '100%', backgroundColor: planAccent(plan.key), borderTopLeftRadius: 8, borderTopRightRadius: 8 }} />
    <CardContent>
      <Typography variant="h5" sx={{ color: '#003366', fontWeight: 800 }}>{plan.title}</Typography>
      <Typography variant="subtitle2" sx={{ color: '#003366', opacity: 0.9, mb: 1 }}>{plan.subtitle}</Typography>
      {plan.description && (
        <Typography variant="body2" sx={{ color: '#111', mb: 1 }}>{plan.description}</Typography>
      )}
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>${plan.priceAnnualUSD.toLocaleString()} / year</Typography>
      <Chip size="small" label={`SLA ${plan.sla}`} sx={{ bgcolor: '#f2f6fb', color: '#003366', fontWeight: 700 }} aria-label={`Service Level Agreement ${plan.sla}`} />
      <Box component="ul" aria-label={`${plan.title} features`} sx={{ mt: 2, pl: 2 }}>
        {plan.features.map((f, idx) => (
          <Box key={idx} component="li" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckIcon fontSize="small" aria-hidden />
            <Typography variant="body2">{f}</Typography>
          </Box>
        ))}
      </Box>
      {plan.featureDetails && plan.featureDetails.length > 0 && (
        <Box sx={{ mt: 1, pl: 1 }} aria-label={`${plan.title} feature details`}>
          {plan.featureDetails.map((d, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
              <strong>{d.title}:</strong> {d.description}
            </Typography>
          ))}
        </Box>
      )}
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" color="primary" href="/contact" aria-label={`Contact sales about ${plan.title}`}>Contact Sales</Button>
      </Box>
    </CardContent>
  </Card>
);

const Pricing: React.FC = () => {
  const { plans, error, save } = usePricingPlans();
  const [editing, setEditing] = useState<PricingPlan | null>(null);
  const [form, setForm] = useState<PricingPlan | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | null>(null);

  const sorted = useMemo(() => [...plans].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [plans]);

  useEffect(() => {
    if (editing) {
      setForm({ ...editing });
    }
  }, [editing]);

  useEffect(() => {
    if (firstFieldRef.current) firstFieldRef.current.focus();
  }, [form]);

  const handleSave = async () => {
    if (!form) return;
    const normalized: PricingPlan = {
      ...form,
      features: (form.features || []).map(f => (typeof f === 'string' ? f.trim() : '')).filter(Boolean)
    };
    await save(normalized);
    setEditing(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <MuiLink
            underline="hover"
            color="inherit"
            href="/dashboard"
            sx={{ display: 'flex', alignItems: 'center' }}
            aria-label="Go to dashboard"
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Dashboard
          </MuiLink>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', color: '#003366', fontWeight: 600 }}>
            Pricing
          </Typography>
        </Breadcrumbs>
        {/* Header styled like About page (h4, USCIS blue, bold) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ color: '#003366', fontWeight: 'bold' }}>
            E‑Unify Pricing
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#003366', mt: 1 }}>
            AI Power Levels — simple annual, seatless pricing. MIT open source remains free for self‑hosting.
          </Typography>
        </Box>

        {error && (
          <Paper sx={{ p: 2, borderLeft: '4px solid #B31B1B', mb: 2 }} role="alert">
            <Typography sx={{ color: '#B31B1B' }}>{error}</Typography>
          </Paper>
        )}

        <Grid container spacing={2} role="list" aria-label="Pricing tiers">
          {sorted.map((p) => (
            <Grid key={p.key} item xs={12} md={4}>
              <PriceCard plan={p} onEdit={(plan) => setEditing(plan)} />
            </Grid>
          ))}
        </Grid>

        {/* Explainer: Why 90-day Pilot vs Monthly */}
        <Paper elevation={0} sx={{ p: 3, mt: 3, border: '1px solid #e0e0e0', backgroundColor: '#f8f9fb' }} aria-labelledby="pilot-vs-monthly-heading">
          <Typography id="pilot-vs-monthly-heading" variant="h6" sx={{ color: '#003366', fontWeight: 800, mb: 1 }}>
            Why a 90‑day Pilot instead of Month‑to‑Month?
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The 90‑day Pilot is a structured evaluation designed to reduce risk and accelerate a go/no‑go decision. It includes onboarding and a conversion credit not available with monthly.
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box sx={{ p: 2, borderLeft: '4px solid #003366', backgroundColor: '#ffffff' }} role="region" aria-label="Monthly access details">
              <Typography variant="subtitle1" sx={{ color: '#003366', fontWeight: 700 }}>Monthly (e.g., $650/mo)</Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                <li><Typography variant="body2">Low‑overhead, ongoing access</Typography></li>
                <li><Typography variant="body2">Fair‑use support (no onboarding package)</Typography></li>
                <li><Typography variant="body2">No automatic credit toward annual</Typography></li>
              </Box>
            </Box>
            <Box sx={{ p: 2, borderLeft: '4px solid #B31B1B', backgroundColor: '#ffffff' }} role="region" aria-label="90-day pilot details">
              <Typography variant="subtitle1" sx={{ color: '#B31B1B', fontWeight: 700 }}>90‑day Pilot (e.g., $1,500/mo × 3 = $4,500)</Typography>
              <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                <li><Typography variant="body2">Guided onboarding (up to 4 hours), SSO validation</Typography></li>
                <li><Typography variant="body2">Success criteria and mid‑point/end reviews</Typography></li>
                <li><Typography variant="body2">Priority during pilot window; reserved capacity</Typography></li>
                <li><Typography variant="body2">Full pilot credit applies to annual conversion (within 30 days), including upgrades</Typography></li>
              </Box>
            </Box>
          </Box>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, mt: 3, bgcolor: '#f9fbff', border: '1px solid #e0e0e0' }}>
          <Typography variant="h6" sx={{ color: '#003366', fontWeight: 800 }}>Open Source (MIT) — Free</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Full source, local/self‑hosting, community support via GitHub issues. No lock‑in; hosted tiers are optional.
          </Typography>
        </Paper>
      </Container>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} aria-labelledby="edit-plan-title" fullWidth maxWidth="sm">
        <DialogTitle id="edit-plan-title">Edit Pricing Plan</DialogTitle>
        <DialogContent dividers>
          <TextField
            inputRef={firstFieldRef}
            margin="dense"
            label="Title"
            fullWidth
            value={form?.title || ''}
            onChange={(e) => setForm(f => ({ ...(f as PricingPlan), title: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Subtitle"
            fullWidth
            value={form?.subtitle || ''}
            onChange={(e) => setForm(f => ({ ...(f as PricingPlan), subtitle: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Plan Description"
            fullWidth
            multiline
            minRows={2}
            value={form?.description || ''}
            onChange={(e) => setForm(f => ({ ...(f as PricingPlan), description: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Annual Price (USD)"
            type="number"
            fullWidth
            value={form?.priceAnnualUSD ?? 0}
            onChange={(e) => setForm(f => ({ ...(f as PricingPlan), priceAnnualUSD: Number(e.target.value) }))}
          />
          <TextField
            margin="dense"
            label="SLA"
            fullWidth
            value={form?.sla || ''}
            onChange={(e) => setForm(f => ({ ...(f as PricingPlan), sla: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Features (one per line)"
            fullWidth
            multiline
            minRows={6}
            value={(form?.features || []).join('\n')}
            onChange={(e) => setForm(f => ({ ...(f as PricingPlan), features: e.target.value.split('\n') }))}
          />
          <TextField
            margin="dense"
            label="Feature Details (Title — Description per line)"
            fullWidth
            multiline
            minRows={6}
            value={((form?.featureDetails || []).map(d => `${d.title} — ${d.description}`)).join('\n')}
            onChange={(e) => setForm(f => ({
              ...(f as PricingPlan),
              featureDetails: e.target.value.split('\n').map(line => {
                const [title, ...rest] = line.split('—');
                return title?.trim() ? { title: title.trim(), description: rest.join('—').trim() } : null;
              }).filter(Boolean) as { title: string; description: string }[]
            }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Pricing;
